// Редът с малките кутийки под хедъра: артикули → сторно → отстъпка → платено.
//
// „Прехвърлено" се появява като пета кутийка САМО когато има прехвърлена сума
// — иначе на всяка обикновена сметка би стояла нула без смисъл, а сметките с
// разделяне не бива да губят числото.

import { useTranslation } from "react-i18next";

import { parseDecimal } from "../../../../components/dataTable";
import { fmtEuro } from "../../../../utils/currency";
import type { OpSummary } from "../../../../api/billOperations";
import { formatDeductedMoney } from "./billOperationsFormat";

type Props = {
  summary: OpSummary;
  isNarrow: boolean;
};

type TotalBox = {
  key: string;
  kicker: string;
  value: string;
  /** Нулата е помътнена — кутийката остава, но не тегли поглед. */
  isZero: boolean;
};

export const BillOperationsTotals = ({ summary, isNarrow }: Props) => {
  const { t } = useTranslation();

  const gross = parseDecimal(summary.gross_revenue);
  const cancelled = parseDecimal(summary.cancelled_amount);
  // discount_amount = −total_discount: отрицателно при отстъпка, ПОЛОЖИТЕЛНО
  // при надбавка — тогава кутийката се казва „Надбавка" и носи явен „+".
  const discount = parseDecimal(summary.discount_amount);
  const transferred = parseDecimal(summary.transferred_amount);
  const paid = parseDecimal(summary.net_revenue);

  const boxes: TotalBox[] = [
    {
      key: "items",
      kicker: t("reportsPage.billOpsTotalItems"),
      value: fmtEuro(gross),
      isZero: gross === 0,
    },
    {
      key: "cancelled",
      kicker:
        summary.cancelled_count > 0
          ? `${t("reportsPage.billOpsTotalCancelled")} · ${summary.cancelled_count}`
          : t("reportsPage.billOpsTotalCancelled"),
      value: formatDeductedMoney(cancelled),
      isZero: cancelled === 0,
    },
    {
      key: "discount",
      kicker: t(
        discount > 0
          ? "reportsPage.billOpsTotalSurcharge"
          : "reportsPage.billOpsTotalDiscount",
      ),
      value: formatDeductedMoney(discount),
      isZero: discount === 0,
    },
  ];

  if (transferred !== 0) {
    boxes.push({
      key: "transferred",
      kicker: t("reportsPage.billOpsTotalTransferred"),
      value: formatDeductedMoney(transferred),
      isZero: false,
    });
  }

  boxes.push({
    key: "paid",
    kicker: t("reportsPage.billOpsTotalPaid"),
    value: fmtEuro(paid),
    isZero: paid === 0,
  });

  return (
    <div
      className={`grid shrink-0 gap-2 border-b border-shade-1 ${
        isNarrow ? "grid-cols-2 px-4 py-3" : "px-6 py-4"
      } ${isNarrow ? "" : boxes.length === 5 ? "grid-cols-5" : "grid-cols-4"}`}
    >
      {boxes.map((box) => (
        <div
          key={box.key}
          className="rounded-[10px] border border-shade-2 bg-surface px-3 py-2.5"
        >
          <p className="truncate font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
            {box.kicker}
          </p>
          <p
            className={`mt-2 font-pos-mono text-[19px] font-extrabold leading-none tabular-nums ${
              box.isZero ? "text-faint" : "text-ink"
            }`}
          >
            {box.value}
          </p>
        </div>
      ))}
    </div>
  );
};
